import pluralize from "pluralize";
import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import { HeaderDivider, HStack, Item, MenuOptionItem, MenuTriggerButton, Section, TriggerButton } from "widgets";

import { BrowserTab } from "@/app-utils/browser-tab.ts";
import { canStartAnalyzerFromSummary, isSaveableType } from "@/app-utils/entity.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import type { Capability, InputTableQuerySpec } from "@/graphql.ts";
import { checkCapability } from "@/modules/users/utils/user.ts";
import { isLicensed } from "@/redux/modules/config.ts";
import type { SelectedDataSeries } from "@/redux/modules/dataset-viewer.ts";
import {
  createUniqueKeyFromTableSpec,
  getTableDataSlice,
  hasSelection,
  type ITableData,
} from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { CAPABILITY, ENTITY_TYPE, FEATURE_DISPLAY_NAME, LICENSE_MODULE, VIEW } from "@/settings/settings.ts";
import { GridReportPlaybooksButton } from "@/ui-modules/core/GridReportPlaybooksButton.tsx";
import KillWorkflowButtonHeader from "@/ui-modules/core/KillWorkflowButtonHeader.js";
import { LockSamplesButton } from "@/ui-modules/core/LockSamplesButton.tsx";
import SamplePlaybooksButton from "@/ui-modules/core/SamplePlaybooksButton.tsx";
import { SaveDialog } from "@/ui-modules/core/save-dialog/SaveDialog.tsx";

import SaveFlagButton from "../genome-viewer/flag-track/SaveFlagDialog.js";
import { CreateUsergroupDialog } from "../projects/CreateUsergroupDialog.js";
import AnalyticalDataExportButton from "./AnalyticalDataExportButton.js";
import { BlastButtonHeader } from "./BlastButton.js";
import ColumnSelectionButton from "./ColumnSelectionButton.js";
import DataEntryButton from "./DataEntryButton.js";
import EditSamplesButton from "./EditSamplesButton.js";
import { ModalActionWidget, PopOverActionWidget } from "./overlays/index.js";
import ParentEditingButton from "./ParentEditingButton.js";
import RangeAnalysisPlaybooksButton from "./RangeAnalysisPlaybooksButton.js";
import ReRunBlastButtonHeader from "./ReRunBlastButton.js";
import ReRunPlaybookButtonHeader from "./ReRunPlaybookButton.js";
import SampleChartButton from "./SampleChartButton.js";
import { ShowRelatedButton } from "./ShowRelatedButton.js";
import TableExportButton from "./TableExportButton.js";
import { ToolsButton } from "./ToolsButton.js";
import UserGroupsAssignmentButton from "./UserGroupsAssignmentButton.js";
import VennDiagramDialog from "./VennDiagramDialog.js";
import { ViewVariantGenomesButton } from "./ViewVariantGenomesButton.js";

// --- Redux ---
function mapStateToProps(state: IAppState) {
  return {
    tableData: state.tableData,
    genomeBrowser: state.genomeBrowser,
    dataViewer: state.datasetViewer,
    configuration: state.config.configuration,
    user: state.auth.session,
    job: state.job,
  };
}

function mapDispatchToProps(_dispatch: ReduxDispatch, _props: IButtonHeaderOwnProps) {
  return {};
}

// --- Button Header - Main Component ---
interface IButtonHeaderOwnProps {
  viewType?: string;
  subViewType?: string;
  portalContext?: string; // useful for giving a context to the portal behavior, so that do not mix up the button placeholders

  hideModifyColumnsBtn?: boolean;
  hideShowRelatedBtn?: boolean;
  hideFlagBtn?: boolean;
  hideBlastBtn?: boolean;
  hideExportBtn?: boolean;
  hideSaveBtn?: boolean;
  hideCopyToClassicPortalBtn?: boolean;
  hideNewUserGroupBtn?: boolean;
  hideUserGroupsAssignmentBtn?: boolean;
  hideVariantGenomesBtn?: boolean;
  showEditParentButton?: boolean;

  // EITHER
  entity?: { id: number; groupType: string }; // has groupType  // only for Summary Pages
  // OR
  tableSpec?: InputTableQuerySpec;
  // OR
  tableSpecs?: InputTableQuerySpec[]; // only for Genome Browser (multiple tracks selectable in the same time)

  // need a name for mapping group of entities to the classic portal
  getParentEntityName?: () => string;
}

class ButtonHeaderImpl extends PureComponent<
  IButtonHeaderOwnProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>
> {
  constructor(props) {
    super(props);
    this.createComparisonButton = this.createComparisonButton.bind(this);
  }

  isContextForGrouping() {
    return (
      this.props.viewType === VIEW.TABLE_VIEW.type ||
      this.props.viewType === VIEW.GENOME_VIEW.type ||
      this.props.viewType === VIEW.CONTENT_BROWSER_VIEW.type
    );
  }

  getParentEntityType(table?: ITableData): string {
    // Entity type for Blast
    // entityType from entity | activeTable  in that order
    return this.props.entity?.groupType ? this.props.entity.groupType : table ? table.tableQuerySpec.resultType : null;
  }

  createColumnSelectionButtonAndDialog(table: ITableData) {
    if (this.props.viewType === VIEW.GENOME_VIEW.type || !table || this.props.hideModifyColumnsBtn) {
      return null;
    }

    return (
      <>
        <ColumnSelectionButton
          key="column-selection-button-placeholder"
          portalContext={this.props.portalContext || createUniqueKeyFromTableSpec(table.tableQuerySpec)}
        />
        <HeaderDivider />
      </>
    );
  }

  createVariantGenomesButton = (table: ITableData) => {
    if (this.props.hideVariantGenomesBtn || this.props.viewType === VIEW.GENOME_VIEW.type || !table) {
      return null;
    }
    // applies only to VCF variants tables (should also support classic variant tables in the future)
    if (table.tableQuerySpec.resultType !== ENTITY_TYPE.VCF_VARIANT) {
      return null;
    }

    const selectedIds: number[] =
      table.selectionModel.selectedIds.length > 0 ? (table.selectionModel.selectedIds as number[]) : [];
    return <ViewVariantGenomesButton table={table} selectedIDs={selectedIds} />;
  };

  render(): ReactElement {
    const activeTable: ITableData =
      this.props.tableSpec && getTableDataSlice(this.props.tableData, this.props.tableSpec);
    const activeTables: ITableData[] = this.props.tableSpecs?.map((tableSpec: InputTableQuerySpec) =>
      getTableDataSlice(this.props.tableData, tableSpec),
    );
    const columnSelectionButton: ReactElement = this.createColumnSelectionButtonAndDialog(activeTable);

    return (
      <HStack spacing={1}>
        {this.createVariantGenomesButton(activeTable)}
        {columnSelectionButton}
        {this.createComparisonButton()}
        {isLicensed(this.props.configuration, LICENSE_MODULE.EXPLORER) && this.createToolsButton(activeTable)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.EXPLORER) && this.createGroupRelatedButton(activeTable)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.TRACKER) && this.createSampleChartButton(activeTable)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.TRACKER) &&
          (checkCapability(this.props.user.capabilities as Capability[], CAPABILITY.SAMPLE_LOCKING) ||
            checkCapability(this.props.user.capabilities as Capability[], CAPABILITY.SAMPLE_UNLOCKING)) &&
          this.createLockSamplesButton(activeTable)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.TRACKER) && this.createEditSamplesButton(activeTable)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.PROCESSOR) && this.createRangeAnalysisButton()}
        {isLicensed(this.props.configuration, LICENSE_MODULE.PROCESSOR) &&
          this.createSamplePlaybooksButtonHeader(activeTable)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.EXPLORER) &&
          this.createReportsPlaybooksButtonHeader(activeTable)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.EXPLORER) && this.createAnalyzeButtonHeader(activeTable)}
        {this.createDataEntryButton(activeTable)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.EXPLORER) &&
          this.createBlastButtonHeader(activeTable, activeTables)}
        {isLicensed(this.props.configuration, LICENSE_MODULE.EXPLORER) && this.createFlagButtonAndDialog()}
        {this.createParentEditingButtonAndDialog()}
        {this.createTableExportButtonAndDialog(activeTable ? [activeTable] : activeTables)}
        {this.createAnalyticalDataExportButtonAndDialog()}
        {this.createSaveButton(activeTable, activeTables)}
        {this.createKillWorkflowButton()}
        {this.createRerunPlaybookEventButton()}
        {this.createNewUserGroupButtonAndDialog()}
        {this.createUserGroupsAssignmentDropdown(activeTable)}
      </HStack>
    );
  }

  isTableExportableType(type: string) {
    return !(type === ENTITY_TYPE.VIEW || type === ENTITY_TYPE.QUERY);
  }

  isSummaryExportableType(type: string) {
    return !(
      type === ENTITY_TYPE.VIEW ||
      type === ENTITY_TYPE.QUERY ||
      type === ENTITY_TYPE.MUTATION ||
      type === ENTITY_TYPE.SAMPLE ||
      type === ENTITY_TYPE.SAMPLE_PARENT ||
      type === ENTITY_TYPE.SEQUENCE ||
      type === ENTITY_TYPE.MARKER ||
      type === ENTITY_TYPE.EVENT
    );
  }

  createBlastButtonHeader(activeTable: ITableData, activeTables: ITableData[]): ReactElement {
    if (this.props.hideBlastBtn || !checkCapability(this.props.user?.capabilities, CAPABILITY.BLAST)) {
      return null;
    }

    return (
      <BlastButtonHeader
        key="analyze-btn-header"
        entityType={this.getParentEntityType(activeTable)}
        genomeBrowser={this.props.genomeBrowser}
        viewType={this.props.viewType}
        entity={this.props.entity}
        activeTables={activeTable ? [activeTable] : activeTables}
      />
    );
  }

  createAnalyzeButtonHeader(activeTable: ITableData): ReactElement {
    const isCoordinateListType: boolean =
      activeTable && activeTable.tableQuerySpec?.resultType === ENTITY_TYPE.COORD_LIST;

    if (isCoordinateListType) {
      let canAnalyzeCoordListsDisabledTooltip = null;
      if (activeTable.selectionModel.selectedIds.length === 0) {
        canAnalyzeCoordListsDisabledTooltip = `Select at least one ${
          FEATURE_DISPLAY_NAME[ENTITY_TYPE.COORD_LIST]
        } to enable this button.`;
      }
      return (
        <MenuTriggerButton
          key="analyze-coord-list"
          icon="query_stats"
          label="Analyze"
          tooltip="Analyze"
          showLabel={true}
          isDisabled={canAnalyzeCoordListsDisabledTooltip}
          onAction={(key) => {
            switch (key) {
              case "send-to-genome-browser": {
                {
                  const selectedIds = activeTable.selectionModel.selectedIds;
                  const organismId = activeTable.tableQuerySpec?.id; // assuming this is a genome summary page
                  window.open(
                    BrowserTab.createUrl(
                      `/organism/${organismId}/genome?add_tracks=${
                        ENTITY_TYPE.COORDINATE
                      }&coordLists=${selectedIds.join(",")}&showGrid=true`,
                    ),
                  );
                }
                break;
              }
            }
          }}
        >
          <Section title="Show selected lists in">
            <Item key="send-to-genome-browser">
              <MenuOptionItem title="Genome Viewer" id="send-to-genome-browser" />
            </Item>
          </Section>
        </MenuTriggerButton>
      );
    }
  }

  createSamplePlaybooksButtonHeader(activeTable: ITableData): ReactElement | undefined {
    const isTableView: boolean = this.props.viewType === VIEW.TABLE_VIEW.type;
    const isSampleType: boolean = activeTable && activeTable.tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE;
    if (isTableView && isSampleType) {
      return <SamplePlaybooksButton key="sample-playbooks-btn-header" activeTable={activeTable} />;
    }
    return undefined;
  }

  createRangeAnalysisButton(): ReactElement | undefined {
    const isGenomeView: boolean = this.props.viewType === VIEW.GENOME_VIEW.type;
    if (isGenomeView) {
      return <RangeAnalysisPlaybooksButton key="range-analysis-btn-header" />;
    }
    return undefined;
  }

  createReportsPlaybooksButtonHeader(activeTable: ITableData): ReactElement | undefined {
    const isTableView: boolean = this.props.viewType === VIEW.TABLE_VIEW.type;
    const isCompatibleType: boolean =
      activeTable?.tableQuerySpec?.resultType === ENTITY_TYPE.VCF_VARIANT ||
      activeTable?.tableQuerySpec?.resultType === ENTITY_TYPE.COORD_LIST ||
      activeTable?.tableQuerySpec?.resultType === ENTITY_TYPE.COORDINATE;
    if (isTableView && isCompatibleType) {
      return <GridReportPlaybooksButton key="report-playbooks-btn-header" activeTable={activeTable} />;
    }
    return undefined;
  }

  createSampleChartButton(activeTable: ITableData): ReactElement | undefined {
    const isTableView: boolean = this.props.viewType === VIEW.TABLE_VIEW.type;
    const isSampleType: boolean = activeTable && activeTable.tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE;
    if (isTableView && isSampleType) {
      return <SampleChartButton key="o-chart-button-key" activeTable={activeTable} />;
    }
    return;
  }

  createDataEntryButton(activeTable: ITableData): ReactElement | undefined {
    const isTableView: boolean =
      this.props.viewType === VIEW.TABLE_VIEW.type || this.props.viewType === VIEW.CONTENT_BROWSER_VIEW.type;
    const isOrganismTable: boolean = activeTable && activeTable.tableQuerySpec?.resultType === ENTITY_TYPE.ORGANISM;
    const dataEntryAllowed: boolean = checkCapability(
      this.props.user?.capabilities as Capability[],
      CAPABILITY.PHENOTYPE_EDITING,
    );
    if (isTableView && isOrganismTable && dataEntryAllowed) {
      return <DataEntryButton key="o-data-entry-button-key" activeTable={activeTable} />;
    }
    return;
  }

  createEditSamplesButton(activeTable: ITableData): ReactElement | undefined {
    const isTableView: boolean = this.props.viewType === VIEW.TABLE_VIEW.type;
    const isSampleType: boolean = activeTable && activeTable.tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE;
    if (isTableView && isSampleType) {
      return <EditSamplesButton key="o-edit-samples-button-key" activeTable={activeTable} />;
    }
    return;
  }

  createLockSamplesButton(activeTable: ITableData): ReactElement | undefined {
    const isTableView: boolean = this.props.viewType === VIEW.TABLE_VIEW.type;
    const isSampleType: boolean = activeTable && activeTable.tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE;
    if (isTableView && isSampleType) {
      return <LockSamplesButton key="o-lock-samples-button-key" activeTable={activeTable} />;
    }
    return;
  }

  createParentEditingButtonAndDialog() {
    if (this.props.showEditParentButton) {
      return <ParentEditingButton viewType={this.props.viewType} summaryPageEntity={this.props.entity} />;
    }
    return null;
  }

  createNewUserGroupButtonAndDialog() {
    if (!this.props.hideNewUserGroupBtn && this.props.viewType === VIEW.USER_GROUP_ADMIN_VIEW.type) {
      return (
        <ModalActionWidget
          title="Create a new user group"
          actionWidget={({ open, isOpen }) => (
            <TriggerButton
              icon="group_add"
              onPress={() => open()}
              tooltip="Create a new user group"
              label="New User Group"
              showLabel
            />
          )}
        >
          {({ close }) => <CreateUsergroupDialog key="new-usergroup-dialog" onApply={close} onCancel={close} />}
        </ModalActionWidget>
      );
    }
    return;
  }

  createRerunPlaybookEventButton() {
    const isJobSummaryViewWithEvent: boolean =
      this.props.viewType === VIEW.BLAST_JOB_VIEW.type && !!this.props.job.job.event;

    // do show the edit/run buttons if we are on one of the event-like, job-related main pages, but not on the sub pages
    const isJobSubView = !!this.props.subViewType;

    const isEventSummaryView: boolean =
      this.props.viewType === VIEW.ENTITY_SUMMARY_VIEW.type && this.props.entity?.groupType === ENTITY_TYPE.EVENT;

    if (isEventSummaryView || (isJobSummaryViewWithEvent && !isJobSubView)) {
      return <>{isJobSummaryViewWithEvent ? <ReRunBlastButtonHeader /> : <ReRunPlaybookButtonHeader />}</>;
    }
    return;
  }

  createKillWorkflowButton() {
    // Workflow is running
    const isEventSummaryView: boolean =
      this.props.viewType === VIEW.ENTITY_SUMMARY_VIEW.type && this.props.entity?.groupType === ENTITY_TYPE.EVENT;

    if (isEventSummaryView) {
      return <KillWorkflowButtonHeader />;
    }
    return;
  }

  createTableExportButtonAndDialog(tables: ITableData[]) {
    const isGenomeView: boolean = this.props.viewType === VIEW.GENOME_VIEW.type;
    const isTableView: boolean = this.props.viewType === VIEW.TABLE_VIEW.type;
    const isSummaryView: boolean = this.props.viewType === VIEW.ENTITY_SUMMARY_VIEW.type;
    const isContentBrowser: boolean = this.props.viewType === VIEW.CONTENT_BROWSER_VIEW.type;
    const isExportableTable: boolean =
      !!tables &&
      !!tables.find((table: ITableData) => this.isTableExportableType(table.tableQuerySpec.resultType)) &&
      this.props.viewType !== VIEW.USER_GROUP_ADMIN_VIEW.type;
    const isExportableSummary: boolean =
      isSummaryView && this.props.entity && this.isSummaryExportableType(this.props.entity.groupType);

    if (
      (!this.props.hideExportBtn && (isGenomeView || ((isTableView || isContentBrowser) && isExportableTable))) ||
      isExportableSummary
    ) {
      return (
        <TableExportButton
          viewType={this.props.viewType}
          tables={tables}
          summaryPageEntity={this.props.entity}
          forceDisabled={isGenomeView && !this.props.genomeBrowser.contig}
          getParentEntityName={this.props.getParentEntityName}
        />
      );
    }
    return null;
  }

  createAnalyticalDataExportButtonAndDialog() {
    const isDataView: boolean = this.props.viewType === VIEW.DATA_VIEW.type;

    if (!this.props.hideExportBtn && isDataView) {
      return <AnalyticalDataExportButton dataViewer={this.props.dataViewer} />;
    }
    return null;
  }

  createFlagButtonAndDialog() {
    if (this.props.viewType !== VIEW.GENOME_VIEW.type || this.props.hideFlagBtn) {
      return null;
    }
    return (
      <SaveFlagButton
        isDisabled={this.props.genomeBrowser.isLoading}
        range={this.props.genomeBrowser.selectedRange ?? this.props.genomeBrowser.range}
        contig={this.props.genomeBrowser.contig}
        userGroups={this.props.genomeBrowser.contig.userGroups}
      />
    );
  }

  createSaveButton(activeTable: ITableData, activeTables: ITableData[]): JSX.Element | null {
    const isUserGroupAdminView: boolean = this.props.viewType === VIEW.USER_GROUP_ADMIN_VIEW.type;
    const isSaveButtonVisible: boolean =
      !this.props.hideSaveBtn &&
      this.getParentEntityType(activeTable) !== ENTITY_TYPE.REGION_FLAG &&
      !isUserGroupAdminView;

    if (!isSaveButtonVisible) {
      return null;
    }

    let isSaveButtonEnabled = true;

    switch (this.props.viewType) {
      case VIEW.DATA_VIEW.type: {
        // data view
        isSaveButtonEnabled =
          this.props.dataViewer.selectedDataSeries.length > 0 &&
          !this.props.dataViewer.selectedDataSeries.some((dataseries: SelectedDataSeries) => dataseries.isLoading) && // none is currently being loaded
          this.props.dataViewer.selectedAnnotations.length > 0;
        break;
      }
      case VIEW.GENOME_VIEW.type: {
        // genome view
        isSaveButtonEnabled = !this.props.genomeBrowser.isLoading && !!this.props.genomeBrowser.contig;
        break;
      }
      case VIEW.ENTITY_SUMMARY_VIEW.type: {
        // summary view
        isSaveButtonEnabled = !!this.props.entity;
        break;
      }
    }

    return (
      <PopOverActionWidget
        title="Save"
        actionWidget={({ open }) => (
          <TriggerButton
            id="table-page-save-button"
            icon="bookmark_add"
            onPress={() => open()}
            isDisabled={!isSaveButtonEnabled}
            label="Save"
            showLabel
          />
        )}
      >
        {({ close }) => this.createSaveDialog(activeTable ? [activeTable] : activeTables, close)}
      </PopOverActionWidget>
    );
  }

  createUserGroupsAssignmentDropdown(activeTable: ITableData) {
    const isUserGroupAdminView: boolean = this.props.viewType === VIEW.USER_GROUP_ADMIN_VIEW.type;

    return !this.props.hideUserGroupsAssignmentBtn && activeTable && isUserGroupAdminView ? (
      <PopOverActionWidget
        title="User Groups"
        actionWidget={({ open }) => (
          <TriggerButton
            icon="manage_accounts"
            onPress={() => open()}
            isDisabled={!hasSelection(activeTable)}
            label="User Groups"
            showLabel
          />
        )}
      >
        {({ close }) => <UserGroupsAssignmentButton activeTable={activeTable} onApply={close} onCancel={close} />}
      </PopOverActionWidget>
    ) : null;
  }

  createSaveDialog(activeTables: ITableData[], close?: () => void) {
    let canSaveGroup = false; // true if the entity type is a valid starting point for grouping
    if (activeTables && activeTables.length > 0) {
      // tableSpec or tableSpecs has content
      if (this.props.tableSpec?.resultType) {
        // From a group table (eg: Proteins ), with one table
        canSaveGroup = isSaveableType(this.props.tableSpec.resultType, this.props.tableSpec.queryType);
      } else if (activeTables.length > 0) {
        // From Feature Tables in Genome View, with N tables
        // true, if some entity in the active tables is saveable
        activeTables.forEach((aT: ITableData) => {
          canSaveGroup = canSaveGroup || isSaveableType(aT.tableQuerySpec.resultType);
        });
      }
    } else if (this.props.entity?.groupType) {
      // From Summary pages of single entities, eg a Protein in a current group
      canSaveGroup = isSaveableType(this.props.entity.groupType);
    }
    return (
      <SaveDialog
        canSaveView={true}
        viewType={this.props.viewType}
        subViewType={this.props.subViewType}
        canSaveGroup={canSaveGroup}
        activeTables={activeTables}
        entityType={this.props.entity?.groupType}
        entity={this.props.entity}
        close={close}
      />
    );
  }

  createComparisonButton() {
    const activeTable: ITableData =
      this.props.tableSpec && getTableDataSlice(this.props.tableData, this.props.tableSpec);
    if (!activeTable || this.props.viewType === VIEW.USER_GROUP_ADMIN_VIEW.type) {
      return;
    }
    const type = this.props.tableSpec.resultType;
    if (
      type !== ENTITY_TYPE.GENE &&
      type !== ENTITY_TYPE.TRANSCRIPT &&
      type !== ENTITY_TYPE.PROTEIN &&
      type !== ENTITY_TYPE.ORGANISM &&
      type !== ENTITY_TYPE.MUTATION &&
      type !== ENTITY_TYPE.SAMPLE &&
      type !== ENTITY_TYPE.VARIANT
    ) {
      return;
    }
    const canCompare: boolean = activeTable.selectionModel.selectedIds.length > 0;
    const disabledTooltipText: string = canCompare ? null : "At least one row must be selected to perform a comparison";
    const tooltipText = `Compare selected ${pluralize.plural(
      FEATURE_DISPLAY_NAME[activeTable.tableQuerySpec.resultType],
    )}`;
    const groupName = `Selected ${pluralizeWord(
      FEATURE_DISPLAY_NAME[activeTable.tableQuerySpec.resultType],
      activeTable.selectionModel.selectedIds.length,
    )} in table`;

    return (
      <ModalActionWidget
        title="Compare"
        actionWidget={({ open }) => (
          <TriggerButton
            icon="compare"
            onPress={() => open()}
            tooltip={tooltipText}
            isDisabled={disabledTooltipText}
            label="Compare"
            showLabel
          />
        )}
      >
        {({ close }) => (
          <VennDiagramDialog
            key="venn-diagram-dialog"
            type={activeTable.tableQuerySpec?.resultType}
            onCancel={close}
            onApply={close}
            explicitSet={activeTable.selectionModel.selectedIds as number[]}
            explicitSetLabel={groupName}
          />
        )}
      </ModalActionWidget>
    );
  }

  createGroupRelatedButton(table: ITableData) {
    if (
      this.props.viewType === VIEW.USER_GROUP_ADMIN_VIEW.type ||
      this.props.viewType === VIEW.GENOME_VIEW.type ||
      !table ||
      this.props.hideShowRelatedBtn ||
      !this.isContextForGrouping()
    ) {
      return null;
    }

    return <ShowRelatedButton table={table} />;
  }

  createToolsButton(table: ITableData): ReactElement {
    const innerEntityType: string = this.getParentEntityType(table);

    if (
      this.props.hideCopyToClassicPortalBtn ||
      (!table && !canStartAnalyzerFromSummary(innerEntityType)) || // from summary page
      (table && !this.isContextForGrouping())
    ) {
      return null;
    }

    return (
      <ToolsButton
        viewType={this.props.viewType}
        table={table}
        entity={this.props.entity}
        innerEntityType={innerEntityType}
        getParentEntityName={this.props.getParentEntityName}
      />
    );
  }
}

const ButtonHeader = connect<
  ReturnType<typeof mapStateToProps>,
  ReturnType<typeof mapDispatchToProps>,
  IButtonHeaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(ButtonHeaderImpl);

export default ButtonHeader;
export { ButtonHeader };
